<template>
  <div class="switcher" :style="elStyle">
    <div :style="selectorCellStyle" class="switcher__selector" :class="selectorClasses">
      <span class="f-w" />
    </div>
    <span
      v-for="(title, index) in titles"
      ref="titles"
      :key="index"
      v-quick-title="hints[index]"
      class="switcher__element"
      :class="{ 'switcher__element--selected': value === index }"
      @click="clicked(index)"
    >
      {{ title }}
    </span>
  </div>
</template>

<script>
/**
 * Switch button. CLick on a title to switch to it and emit the input event
 * v-model: index of the selected title in the titles array
 *
 * @vue-prop {Boolean} [useFixedSize] - Use a fixed size for items in the switcher.
 */

export default {

  name: 'Switcher',

  props: {
    value: {
      type: Number,
      default: 0,
    },
    titles: {
      type: Array,
      required: true,
    },
    hints: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    useFixedSize: Boolean,
  },

  data() {
    return {
      isMounted: false,
      mountSizeSet: false,
    };
  },

  computed: {
    /** Object: CSS Style applied to selector overlay */
    selectorCellStyle() {
      if (this.isMounted && this.$refs.titles && this.$refs.titles[this.value] && this.$el) {
        const elRect = this.$el.getBoundingClientRect();
        const rect = this.$refs.titles[this.value].getBoundingClientRect();
        return {
          '--left': `${rect.left - elRect.left}px`,
          '--selector-size': this.useFixedSize ? undefined : `${rect.width}px`,
        };
      }
      return {};
    },
    /** Object: Style of the this.$el */
    elStyle() {
      return {
        '--size': this.useFixedSize ? undefined : 'auto',
      };
    },
    /** Object: Classes applied to the selector overlay */
    selectorClasses() {
      return {
        'switcher__selector--animatable': this.mountSizeSet,
      };
    },
  },

  watch: {
    '$store.getters.fontSize': function () {
      // Refresh the size of the selector in case the 'em' size changes
      this.isMounted = false;
      this.$nextTick(() => {
        this.isMounted = true;
      });
    },
  },

  mounted() {
    this.isMounted = true;
    setTimeout(() => {
      // Prevent animating switchers when mounting them
      // The first position and width should be set without animation
      this.mountSizeSet = true;
    }, 500);
  },

  methods: {
    clicked(index) {
      if (!this.disabled) {
        this.$emit('input', index);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.switcher {
  --size: 8rem;
  --selector-size: 8rem;
  $main-switcher-padding: 2px;

  position: relative;
  display: inline-flex;
  align-items: center;
  font-size: $font-size-main;
  line-height: 1.4rem;
  border-radius: 10rem;
  background: $color-gray-background;
  padding: $main-switcher-padding;

  &__element {
    width: var(--size);
    padding: 0.4rem 1.2rem;
    box-sizing: border-box;
    text-align: center;
    flex: 0 0 auto;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    border-radius: 10rem;
    z-index: 10001;
    cursor: pointer;
    transition: color .2s;

    &--selected {
      color: #fff;
    }
  }

  &__selector {
    position: absolute;
    top: $main-switcher-padding;
    bottom: $main-switcher-padding;
    left: var(--left);
    width: var(--selector-size);
    background: $color-shizen-blue;
    border-radius: 10rem;

    &--animatable {
      transition: left .2s, width .2s;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .switcher {
    font-size: $font-size-smartphone;

    &__element {
      padding: 0.3rem 1rem;
    }
  }
}
</style>
